/**
 * Condition Step Executor — Boolean branching in workflows
 *
 * Config:
 * - field: string (dot-path into pipelineData)
 * - operator: TriggerOperator
 * - value: unknown
 * - trueSteps: string[] — step IDs to execute if condition is true
 * - falseSteps: string[] — step IDs to execute if condition is false
 */

import type { EngineStep, EngineExecutionContext, StepExecutionResult, StepExecutor } from '../types';

export const conditionStepExecutor: StepExecutor = {
  type: 'condition',
  async execute(step: EngineStep, context: EngineExecutionContext): Promise<StepExecutionResult> {
    try {
      const field = step.config.field as string;
      const operator = step.config.operator as string;
      const value = step.config.value;
      const trueSteps = (step.config.trueSteps as string[]) || [];
      const falseSteps = (step.config.falseSteps as string[]) || [];

      if (!field || !operator) {
        return { success: false, error: 'field and operator are required for condition step' };
      }

      const fieldValue = resolveDataPath(field, context.pipelineData);
      const result = evaluate(fieldValue, operator, value);

      return {
        success: true,
        output: { conditionResult: result, evaluatedField: field, evaluatedValue: fieldValue },
        nextStepIds: result ? trueSteps : falseSteps,
      };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Condition evaluation failed',
      };
    }
  },
};

function resolveDataPath(path: string, data: Record<string, unknown>): unknown {
  const parts = path.split('.');
  let current: unknown = data;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

function evaluate(fieldValue: unknown, operator: string, value: unknown): boolean {
  switch (operator) {
    case 'eq': return fieldValue === value;
    case 'neq': return fieldValue !== value;
    case 'gt': return typeof fieldValue === 'number' && typeof value === 'number' ? fieldValue > value : false;
    case 'lt': return typeof fieldValue === 'number' && typeof value === 'number' ? fieldValue < value : false;
    case 'contains':
      if (Array.isArray(fieldValue)) return fieldValue.includes(value);
      if (typeof fieldValue === 'string' && typeof value === 'string') return fieldValue.includes(value);
      return false;
    case 'in': return Array.isArray(value) ? value.includes(fieldValue) : false;
    case 'exists': return fieldValue !== undefined && fieldValue !== null;
    case 'regex':
      if (typeof fieldValue === 'string' && typeof value === 'string') {
        try { return new RegExp(value).test(fieldValue); } catch { return false; }
      }
      return false;
    default: return false;
  }
}
